import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { usePageMeta } from '@/hooks/usePageMeta';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import PageHeader from '@/components/ui/PageHeader';
import AdUnit from '@/components/ads/AdUnit';
import './AdvicePage.css';

interface AdviceArticle {
  id: string;
  category: string;
  emoji: string;
  title: string;
  summary: string;
  content: string;
  organic: boolean;
  tags: string[];
}

const ARTICLES: AdviceArticle[] = [
  {
    id: 'crop-rotation',
    category: 'Soil & Planning',
    emoji: '🔄',
    title: 'The Four-Year Crop Rotation',
    summary: 'The cornerstone of organic kitchen gardening — why rotating your crops is essential for soil health, disease prevention, and fertility.',
    organic: true,
    tags: ['planning', 'soil', 'disease-prevention'],
    content: `## Why Rotate?

Crop rotation is the practice of growing different plant families in different areas of the garden each year, following a structured sequence. It is the single most important technique in the organic kitchen garden, and its neglect leads inexorably to build-up of soil-borne pests and diseases, declining fertility, and poor harvests.

The principle is simple: plants in the same family are susceptible to the same pests and diseases, and they share similar nutrient requirements. Move them each year, and the pest populations that built up last year cannot find their host plants. The soil has time to recover.

## The Four Groups

A typical four-year rotation divides vegetables into four groups:

**Group 1: Brassicas** (cabbage, kale, broccoli, cauliflower, Brussels sprouts, radish, swede, turnip)
- Most demanding of fertility
- Highly susceptible to club root (Plasmodiophora brassicae) — a soil-borne disease that persists for up to 20 years
- Need firm soil and high nitrogen
- Precede with: green manure, then lime to raise pH

**Group 2: Roots** (carrot, parsnip, beetroot, potato, onion, leek, garlic)
- Light feeders — do NOT add fresh manure
- Fresh manure causes forking in carrots and parsnips
- Potatoes: follow a well-manured bed from the previous year
- Onion family: susceptible to white rot — strict rotation essential

**Group 3: Legumes** (peas, beans, broad beans, runner beans, French beans)
- Nitrogen fixers — leave valuable fertility for the crop that follows
- Relatively undemanding of added nutrients
- Fix nitrogen through Rhizobium bacteria on their roots — always dig the roots in at season end
- Follow with: brassicas, which will benefit from the nitrogen

**Group 4: Potatoes / Miscellaneous** (courgettes, squash, sweetcorn, salads, tomatoes, celery)
- Grown on the most fertile ground
- Potatoes clean the ground through earthing up and physical disturbance
- Cucurbits and tomatoes: heavy feeders, benefit from plenty of organic matter

## In Practice

Mark your beds 1–4 and rotate clockwise each year. Group 1 follows Group 4, Group 2 follows Group 1, and so on. After four years, each group returns to its starting bed — by which time any club root spores or onion white rot will be greatly reduced.

## Historical Bad Practice

Before the understanding of soil-borne disease, monoculture was the norm in large-scale agriculture. The Irish Potato Famine (1845–1852) was the direct consequence of growing a single variety of potato on the same ground year after year — when blight arrived, there was nothing to resist it. Modern intensive agriculture still largely ignores rotation, relying instead on soil fumigants, fungicides, and nematicides — chemical solutions to problems that rotation would largely prevent.`,
  },
  {
    id: 'compost',
    category: 'Soil & Planning',
    emoji: '♻️',
    title: 'Making Good Compost',
    summary: 'Compost is the foundation of the organic garden. Learn how to make the best compost from your kitchen and garden waste.',
    organic: true,
    tags: ['compost', 'soil', 'fertility'],
    content: `## The Alchemy of Composting

Compost is decayed organic matter — the product of millions of micro-organisms, fungi, worms, and invertebrates breaking down raw material into humus. A well-made compost heap transforms "waste" into one of the most valuable substances you can add to garden soil.

Good compost improves soil structure, feeds micro-organisms, retains moisture, buffers pH, suppresses disease, and provides a slow, balanced release of plant nutrients. It is the organic gardener's most important input.

## The Carbon:Nitrogen Ratio

The key to good compost is the balance between carbon-rich "browns" and nitrogen-rich "greens":

**Browns (carbon-rich):** Cardboard, straw, dry leaves, wood chippings, newspaper, hay, bracken, untreated sawdust

**Greens (nitrogen-rich):** Fresh grass clippings, vegetable peelings, tea leaves, coffee grounds, fresh plant material, animal manures

Aim for roughly 3 parts carbon to 1 part nitrogen by volume. Too much nitrogen and the heap turns slimy and smells of ammonia. Too much carbon and it breaks down slowly without generating heat.

## Building the Heap

Build in layers: a layer of browns, a layer of greens, repeat. Alternate in thin layers rather than adding large amounts of one material at once. Water if very dry. A well-built heap will heat up to 60–70°C within days — hot enough to kill weed seeds and pathogens.

## What NOT to Compost

- Cooked food scraps, meat, fish, dairy (attract vermin)
- Diseased plant material (club root, blight-infected haulms, white rot — these survive the heap)
- Perennial weed roots (bindweed, ground elder, couch grass — these survive too)
- Anything treated with persistent herbicides

## Turning and Timing

Turn the heap after 4–6 weeks — this aerates it, brings the outer cooler material to the centre, and accelerates decomposition. A heap turned 2–3 times will produce finished compost in 3–4 months in summer, or over winter. If you don't turn, expect 9–12 months.

## Historical Perspective

Before the arrival of synthetic fertilisers (which became widely available after World War II, as ammonia factories converted from making explosives to making nitrate fertilisers), composting was not an "alternative" practice — it was the only practice. Every farm and household managed its organic matter carefully. The abandonment of composting in favour of bought-in synthetic fertilisers has led to catastrophic declines in soil organic matter, soil life, and soil structure across agricultural land worldwide.`,
  },
  {
    id: 'companion-planting',
    category: 'Growing Techniques',
    emoji: '🤝',
    title: 'Companion Planting — What the Evidence Shows',
    summary: 'Which companion planting combinations really work? An honest look at the evidence behind popular combinations.',
    organic: true,
    tags: ['companion-planting', 'pest-control', 'biodiversity'],
    content: `## The Claims and the Evidence

Companion planting — the deliberate growing of different plants in proximity for mutual benefit — has a long history in horticulture and a devoted following among organic growers. But the evidence for many popular combinations is thinner than often claimed. Here is an honest assessment.

## Combinations with Good Evidence

**Alliums with carrots** — well-supported. The smell of onions, leeks, or chives confuses carrot fly (Psila rosae), which locates its host by smell. Research shows mixed rows or alternating beds of carrots and alliums reduce carrot fly damage significantly.

**Legumes as nitrogen fixers** — excellent evidence. Peas and beans, through symbiosis with Rhizobium bacteria on their roots, fix atmospheric nitrogen. Growing them before nitrogen-demanding brassicas is sound practice with solid scientific backing.

**African marigolds (Tagetes) against soil nematodes** — genuine effect. Tagetes roots release alpha-terthienyl, a compound that suppresses root-knot nematodes. Particularly valuable as a preceding crop in glasshouses before tomatoes.

**Nasturtiums as sacrificial plants** — reasonable evidence. Aphids are attracted to nasturtiums and may colonise them in preference to crops nearby. Whether this actually protects crops or merely concentrates pests is debated, but many growers report benefit.

## Claims That Need Caution

**Basil improves tomato flavour** — popular belief, no scientific evidence. The two make excellent companions aesthetically, and both need similar conditions. Whether volatile compounds from basil genuinely affect tomato biochemistry has not been demonstrated.

**Roses and garlic** — growers swear by this, evidence is limited. Garlic as a fungicide and bactericide is well-established; whether growing garlic near roses actually reduces black spot is anecdotal.

## What Companion Planting Genuinely Achieves

Even where specific repellent or attraction claims are unproven, mixed planting genuinely:

- Increases biodiversity, supporting beneficial insects
- Creates a varied microclimate less amenable to pest outbreaks
- Reduces soil-borne disease pressure (monoculture concentrates problems)
- Makes better use of space and vertical growing opportunities

The most honest position is that companion planting is a valuable part of ecological growing, and the evidence for several key combinations is strong — but it should not be relied upon as the sole pest management strategy.`,
  },
  {
    id: 'no-dig',
    category: 'Soil & Planning',
    emoji: '🪱',
    title: 'No-Dig Gardening',
    summary: 'Why disturbing your soil less can lead to better growing — the science and practice of no-dig gardening.',
    organic: true,
    tags: ['no-dig', 'soil', 'worms', 'technique'],
    content: `## The Principle

No-dig gardening — growing vegetables and other crops without digging or tilling the soil — is not a new idea, but it has been popularised and rigorously championed by growers like Charles Dowding in recent decades. The central insight is that healthy soil has a complex structure built by fungi, worms, and micro-organisms that digging destroys.

## What Digging Does

Digging breaks the mycelial networks of soil fungi — particularly the mycorrhizal fungi that form symbiotic relationships with plant roots, extending their effective reach enormously. It destroys the tunnels and passages created by worms and other soil invertebrates. It exposes dormant weed seeds to light, triggering their germination. And it disrupts the soil food web — the complex community of bacteria, fungi, protozoa, nematodes, and larger invertebrates that cycle nutrients and build soil structure.

## The No-Dig Method

Instead of digging, no-dig gardening builds fertility from above:

1. In autumn or early spring, lay cardboard directly over the existing surface (including weeds)
2. Cover with 10–15cm of well-rotted compost, manure, or a mixture
3. Plant or sow directly into the compost layer
4. Repeat annually — top-dress with 2–5cm of compost each autumn

The cardboard suppresses weeds as it breaks down. The compost is pulled down into the soil by worms. The soil structure is preserved.

## Evidence and Results

Charles Dowding's long-term trial plots at Homeacres, comparing dug and undug beds side by side over many years, consistently show equal or better yields from no-dig beds, with significantly fewer weeds. The initial weed suppression from cardboard reduces the annual work of weeding dramatically.

## Implications for Biodynamic Practice

No-dig aligns well with biodynamic thinking — the application of Preparation 500 to the soil surface, the preservation of soil life, the building of humus from above — all are compatible with and enhanced by a no-dig approach.`,
  },
  {
    id: 'water-organic',
    category: 'Growing Techniques',
    emoji: '💧',
    title: 'Water Wisely — Organic Irrigation Principles',
    summary: 'How, when, and how much to water your vegetable garden for best results — with advice on drought tolerance and water conservation.',
    organic: true,
    tags: ['watering', 'drought', 'mulching', 'technique'],
    content: `## When to Water

The best time to water is early morning, when temperatures are lower and evaporation rates are minimal. Water applied in the morning has time to penetrate to root depth before the heat of the day. Evening watering can leave foliage wet overnight, encouraging fungal diseases.

Water deeply and infrequently rather than little and often. A light sprinkle encourages shallow roots near the surface; a thorough soak once or twice a week encourages roots to grow deep into the soil, where moisture is more consistent and the plant becomes more drought-tolerant.

## What to Water

Focus water at the root zone, not on the leaves. A slow trickle from a watering can at the base of each plant is far more effective than a hose sprinkler that wets everything.

Certain crops have a critical watering period:
- **Peas and beans:** water heavily when in flower and when pods are forming
- **Tomatoes:** consistent watering prevents blossom end rot and fruit splitting; irregular watering causes blossom end rot
- **Courgettes and cucumbers:** high water demand throughout the season
- **Root crops:** consistent moisture for even root development; drought causes splitting when rain returns

## Mulching to Conserve Moisture

Mulching — covering the soil surface with a layer of material — is the most effective way to reduce water loss. A 7–10cm layer of straw, composted wood chip, or well-rotted compost reduces evaporation dramatically, suppresses weeds, and moderates soil temperature.

Apply mulch when the soil is already moist. Mulching dry soil traps the drought in.

## Historical Bad Practice

The invention of overhead irrigation systems in the 20th century, while enormously productive, brought significant problems. Wet foliage is more susceptible to disease; irrigation from aquifers at unsustainable rates is depleting groundwater globally. Drip irrigation, which delivers water directly to root zones, is a more efficient and sustainable alternative.`,
  },
  {
    id: 'hotbeds',
    category: 'Growing Techniques',
    emoji: '🔥',
    title: 'Hotbeds — Using Fermenting Manure for Early Crops',
    summary: 'An ancient technique rediscovered by organic growers — how the heat of fermenting horse manure can give you salads and seedlings weeks ahead of the season.',
    organic: true,
    tags: ['hotbeds', 'manure', 'early-crops', 'technique'],
    content: `## What Is a Hotbed?

A hotbed is a growing frame whose base is filled with fresh, fermenting horse manure. As the manure breaks down, it generates heat — enough to maintain temperatures of 20–25°C inside the frame through cold spring nights, weeks before the soil outdoors has warmed. It is one of the oldest techniques in kitchen gardening, widely used before glass was cheap enough for heated glasshouses, and it requires no electricity, no fuel, and no cost beyond the manure itself.

## How It Works

Fresh horse manure — particularly when mixed with straw bedding — contains enormous populations of bacteria and fungi. As these micro-organisms break down the organic matter, the process is exothermic: it generates heat. A well-built hotbed can reach 60–70°C in its first week before settling to the 20–25°C growing range. The art is to allow the initial heat to pass before placing tender plants.

## Building a Hotbed

1. Collect fresh horse manure with a good proportion of straw — the fresher the better. Avoid poultry manure for this purpose; it heats unevenly and burns more quickly.
2. Build a mound or pit 45–60cm deep, slightly larger than your cold frame. Firm it gently as you build.
3. Place the cold frame on top and close the lid.
4. Wait 5–7 days for the initial violent heat to pass. Check with a thermometer — you want 25°C or below before sowing.
5. Add a layer of good compost or topsoil (10–15cm) over the manure, then sow or plant directly.

## What to Grow

Hotbeds are ideal for early lettuce, radish, spring onions, and mustard greens from January or February. They are also excellent for starting off tomato, pepper, aubergine, and cucumber seedlings in March — all crops that need warmth to germinate and establish.

## Manuring the Garden

When the hotbed is spent — typically after 8–10 weeks — the manure has lost its heat but has partially composted into an excellent soil amendment. Spread it on beds where brassicas or hungry feeders will grow.

## Historical Note

Hotbeds were standard practice in walled kitchen gardens serving large houses throughout the 17th, 18th, and 19th centuries. Head gardeners would time their manure deliveries carefully to have successions of hotbeds ready through late winter and spring. The ability to produce cucumbers and melons in February was considered a mark of exceptional skill.`,
  },
  {
    id: 'wormery',
    category: 'Soil & Planning',
    emoji: '🪱',
    title: 'How to Build a Wormery',
    summary: 'Worm composting turns kitchen scraps into the richest possible compost and a potent liquid feed — and it can be done in the smallest space.',
    organic: true,
    tags: ['wormery', 'compost', 'soil', 'fertility'],
    content: `## Why a Wormery?

A wormery — also called a vermicomposter — uses red brandling worms (Eisenia fetida) rather than the common earthworm to rapidly digest kitchen waste. The result is worm castings: an extraordinarily fertile, fine-textured compost that has higher levels of available nitrogen, phosphorus, and potassium than conventional compost, and a liquid "worm tea" that diluted and applied as a feed is one of the best liquid fertilisers available.

A wormery is ideal for small gardens, flats, and anyone who generates more kitchen waste than a conventional compost heap can handle. It takes up minimal space and, if managed well, produces no smell.

## Building a Simple Wormery

A wormery can be bought ready-made or made from two stacking plastic boxes:

1. Take a large opaque plastic box with a lid. Drill drainage holes in the base and ventilation holes around the upper sides.
2. Place this inside a second, identical box to catch liquid drainage — this is your worm tea.
3. Add 10cm of bedding material: torn cardboard, damp newspaper, or coir. This should feel like a wrung-out sponge.
4. Add your worms — at least 500g of red brandlings. These are not garden earthworms; they must be the surface-dwelling species available from fishing suppliers or wormery retailers.
5. Add a thin layer of kitchen scraps and cover with a piece of damp cardboard or newspaper.

## What to Feed

**Yes:** Vegetable and fruit peelings, coffee grounds, tea bags (staple-free), eggshells, bread in small amounts, crushed cardboard.

**No:** Meat, fish, dairy, citrus in large quantities, onion in large quantities (acidic, worms dislike it), anything cooked in oil, glossy paper.

## Managing the Wormery

Keep the wormery in a sheltered spot — worms are most active between 15–25°C and will die if frozen. In summer, keep it in shade. In winter, insulate it with old carpet or bubble wrap or bring it into a garage.

Feed little and often — a handful every 2–3 days is better than a large dump weekly. If the wormery smells, it is too wet or has too much nitrogen-rich material: add more torn cardboard and reduce feeding temporarily.

## Using the Outputs

Worm castings can be used as a seed compost, potting compost additive, or top-dressing around plants. The liquid that collects in the base should be diluted 10:1 with water before use as a liquid feed — undiluted it can scorch roots.

## Worms in the Garden

Red brandlings do not thrive in open soil — they are surface-dwellers, not deep burrowers. However, spent wormery compost added to the garden carries millions of bacteria and fungi that genuinely improve soil biology.`,
  },
  {
    id: 'potato-clamp',
    category: 'Harvesting & Storage',
    emoji: '🥔',
    title: 'Storing Potatoes — The Potato Clamp',
    summary: 'How to store a glut of potatoes through winter using traditional methods — from the kitchen store to the outdoor clamp.',
    organic: true,
    tags: ['potatoes', 'storage', 'harvesting', 'preserving'],
    content: `## Harvesting for Storage

Potatoes destined for winter storage should be left in the ground until the haulm (top growth) has died back completely. This signals that the skins have set and will resist bruising and disease in store. Choose a dry day for lifting; wet potatoes store poorly and rot quickly.

Inspect every potato as you lift. Any that are damaged by the fork, blemished with blight lesions, slug holes, or soft patches should be set aside for immediate use. Only perfect, unblemished tubers go into long-term storage.

## Green Potatoes

Any potato that has been exposed to light — either in the ground (if earthing up was inadequate) or after lifting — will have developed solanine, a toxic alkaloid that makes the potato bitter and potentially harmful. Green or greenish potatoes should not be eaten. Greening can be prevented entirely by thorough earthing up during the growing season and by storing in complete darkness.

## Storing in Hessian or Paper Sacks

The simplest method for smaller quantities is to store in hessian or thick paper sacks (never plastic, which traps moisture and encourages rot). Place in a cool, dark, frost-free building — an unheated outbuilding, cellar, or garage. Temperature should be 4–7°C. Check monthly and remove any that are rotting — one bad potato genuinely will spread to its neighbours.

## The Outdoor Clamp

For larger quantities, the traditional outdoor clamp is effective and requires no special buildings. It was the standard storage method for farm-scale potato crops across Britain and Northern Europe before the era of refrigeration.

1. Choose a well-drained site. Lay a thick bed of straw (20–30cm) on the ground.
2. Pile the potatoes in a conical heap on the straw, with the narrowest point at the top.
3. Cover the heap completely with straw (another 20–30cm).
4. Cover the straw with excavated soil, packed firmly. Leave a tuft of straw protruding from the top as a ventilation chimney.
5. In hard frosts, add a second layer of soil or extra straw around the base.

Clamps maintain a stable temperature around 4–6°C through their insulating mass and the slight heat generated by the potatoes themselves. They are surprisingly effective even in severe winters.

## Chitting

Potatoes removed from store in late winter for spring planting should be "chitted" — placed in egg boxes or seed trays in a cool, light (but frost-free) place to encourage short, sturdy green shoots before planting. This gives them a head start.`,
  },
  {
    id: 'raised-beds',
    category: 'Soil & Planning',
    emoji: '🪵',
    title: 'Raised Bed Gardening',
    summary: 'Why raised beds are the most productive system for the kitchen garden — how to build, fill, and manage them for year-round cropping.',
    organic: true,
    tags: ['raised-beds', 'planning', 'soil', 'productivity'],
    content: `## The Case for Raised Beds

The raised bed system — growing in defined, permanently made beds that are never walked on — is the most productive, labour-efficient approach to the kitchen garden. The benefits compound over time: soil structure improves because it is never compacted, fertility builds because organic matter is applied to a smaller area, and weeds are easier to manage in a clearly defined space.

Raised beds can be at ground level (simply defined areas never walked on) or built up with boards to create a raised growing surface. Building up is worth doing: it improves drainage, warms faster in spring, is easier to work on, and makes sense on compacted or poor soils.

## Dimensions

The critical dimension is width: beds should be no wider than you can comfortably reach to the centre from both sides — typically 1.2m (4 feet). This means you never need to step onto the growing surface. Length is flexible — 2.4m (8 feet) is a common practical choice that allows easy access from the ends.

Paths between beds should be at least 45cm (18 inches) — wide enough to work comfortably, kneel, and manoeuvre a wheelbarrow.

## Building the Beds

Boards can be of any rot-resistant timber: oak, larch, Douglas fir, or untreated hardwood. Avoid pressure-treated softwood, as the preservatives may leach into food crops. Railway sleepers are popular but can leach creosote. Recycled scaffolding boards are a practical and economic choice.

Height of 20–30cm (8–12 inches) is usually sufficient. Deeper is not necessary unless you are building over very poor subsoil or hardcore.

## Filling and Soil Building

Fill new raised beds with a mixture of topsoil and well-rotted compost. For a bed built over grass or weeds, lay cardboard first to suppress growth — no need to dig. Each autumn, top-dress beds with 5–7cm of compost, leaf mould, or well-rotted manure. This is the only input the system needs.

## Intensive Planting

The absence of paths within the growing area means you can plant more densely than traditional row gardening. Following 'equidistant' rather than 'in rows' spacing — spacing plants so each is equidistant from its neighbours — maximises use of space and creates a canopy that suppresses weeds naturally.`,
  },
  {
    id: 'veg-plot-planning',
    category: 'Soil & Planning',
    emoji: '📐',
    title: 'Planning and Building Your Vegetable Plot',
    summary: 'How to design a kitchen garden from scratch — siting, sizing, dividing, and preparing the ground for first-year and long-term productivity.',
    organic: true,
    tags: ['planning', 'layout', 'new-garden', 'design'],
    content: `## Choosing the Site

The three most important factors in siting a kitchen garden are: sunlight, shelter, and drainage.

**Sunlight:** Most vegetables need at least six hours of direct sun per day. A south or south-west facing aspect is ideal. Avoid the shadow of buildings, walls, or trees — even partial shade dramatically reduces yields of fruiting crops like tomatoes, squash, and beans.

**Shelter:** A sheltered site is more productive than an exposed one. Cold winds reduce growth, damage plants physically, and increase water loss. A hedge (which filters wind rather than creating turbulence) is better than a solid fence as a windbreak. Ensure the shelter does not create shade.

**Drainage:** Few crops will thrive in waterlogged soil. Choose a site where water does not stand after heavy rain. If your site is heavy clay or has poor drainage, raised beds are the answer.

## Sizing Realistically

New gardeners consistently overestimate how much ground they can manage. A 4m × 4m plot (16 square metres) is a good starting point for a family, producing a meaningful yield of salad, root vegetables, and some main crops without becoming overwhelming. Expand only when you have the first area under control.

## The Permanent Framework

Design for the long term with permanent features:
- **Paths** wide enough to kneel and work from (minimum 45cm)
- **Beds** no wider than 1.2m (you must reach the centre from both sides)
- **A compost area** — two or three bays, large enough to handle all garden waste
- **A water source** — a water butt or tap nearby saves enormous effort

## Preparing the Ground

For a new site on grass or weeds:
1. In autumn, lay thick cardboard over the entire area. Overlap edges generously.
2. Cover with at least 15cm of compost, topsoil, or a mix.
3. Leave over winter — by spring, the cardboard has killed the grass and begun to rot.
4. Mark out beds and paths. Plant directly into the compost layer.

This no-dig approach is far less labour-intensive than digging and produces excellent results in the first year.

## The Four-Year Plan

From the start, design your beds in groups of four for crop rotation. Number them, keep records, and follow the rotation faithfully. The discipline of record-keeping — even a simple notebook — transforms the vegetable garden from reactive management to intentional cultivation.`,
  },
  {
    id: 'three-sisters',
    category: 'Growing Techniques',
    emoji: '🌽',
    title: 'Three Sisters Planting',
    summary: 'The ancient Native American intercropping system of maize, beans, and squash — and why it works so well in the organic garden.',
    organic: true,
    tags: ['three-sisters', 'companion-planting', 'intercropping', 'technique'],
    content: `## Origins

The Three Sisters is an intercropping system developed by Native American peoples — most prominently the Haudenosaunee (Iroquois) and Mesoamerican civilisations — over thousands of years. The three crops are maize (sweet corn), climbing beans, and squash (or pumpkin). Grown together, each provides something the others need: a system so well-integrated it produced the nutritional and caloric foundation of many civilisations.

## How the System Works

**Maize** grows tall first and provides a living pole for the beans to climb. It is a heavy feeder but is given the nitrogen it needs by its companion.

**Climbing beans** fix atmospheric nitrogen through Rhizobium bacteria on their roots. As the beans grow, they replenish what the maize removes, maintaining fertility in the bed.

**Squash** spreads low along the ground, its large leaves creating a living mulch. This suppresses weeds, shades the soil to retain moisture, and deters some pests — particularly the spiny-leaved varieties, whose rough foliage is unwelcoming to slugs.

Together, the three crops create a self-sustaining system: nitrogen fixed, water conserved, pests deterred, vertical space fully exploited.

## Nutritional Synergy

Maize provides carbohydrate (starch, energy) but is deficient in the amino acid lysine. Beans are rich in lysine and provide protein. Squash provides beta-carotene, vitamins, and mineral density. The combination produces a balanced, complete nutrition — one reason the system sustained large populations for millennia.

## Growing Three Sisters in Britain

Britain's shorter, cooler season requires some adaptation:

- Start sweet corn indoors in April. Harden off carefully — it is frost-sensitive.
- Plant out after last frost (late May), spacing 45cm apart in a block (not a row — maize is wind-pollinated and needs proximity).
- Plant one bean plant at the base of each corn plant once the corn is 15–20cm tall.
- Plant squash at the edges of the block to spread outward into paths.

The system requires a warm, sunny site and performs best in a hot summer. In cooler years, sweet corn may not set reliably — French climbing beans are a more reliable choice than runner beans, which prefer cooler conditions.

## Yield and Space

Three Sisters is not the highest-yielding system for any individual crop, but the combined yield per unit of space is remarkable. The combination of corn, beans, and squash from one bed represents a genuinely productive intercrop, and it builds soil fertility rather than depleting it.`,
  },
  {
    id: 'seed-sowing-timing',
    category: 'Seeds & Propagation',
    emoji: '📅',
    title: 'Seed Sowing — Getting the Timing Right',
    summary: 'When to sow what, and why getting the timing right is more important than almost any other decision in the kitchen garden.',
    organic: true,
    tags: ['seeds', 'sowing', 'timing', 'propagation'],
    content: `## The Most Common Mistake

The single most common mistake made by new kitchen gardeners is sowing too early. The instinct to start as soon as possible in late winter is understandable, but leggy, starved seedlings raised in insufficient light are a poor beginning to the season. Cold soil that fails to germinate seeds, or seedlings that sit still for weeks in pots, give no advantage over seeds sown at the correct time.

## The Sowing Calendar

Timing is governed by three factors: whether the crop needs indoor propagation, the last expected frost date in your area, and soil temperature.

**January–February (indoors only):** Onions and leeks (slow-growing and benefit from early start), chillies and peppers (long season required), broad beans (in pots to transplant early).

**March (indoors, with care):** Tomatoes (8 weeks before last frost), aubergines, sweet peppers, celery and celeriac, early lettuce (for cold frame planting).

**April (indoors, and outdoors under cover):** Courgettes and cucumbers (3–4 weeks before planting out), sweet corn (2–3 weeks before planting out), basil. Outdoors under fleece: beetroot, carrots, parsnips, peas, spinach, lettuce.

**May (outdoors direct):** After last frost: beans (French, runner), courgettes, squash. Directly: beetroot, carrots, chard, lettuce, rocket, turnip.

**June–July:** Successional sowings of salad, beetroot, French beans for autumn harvest. Autumn/winter brassicas sown in seedbed for transplanting.

## Soil Temperature

Seeds will not germinate in cold soil. Most vegetables need a minimum soil temperature to germinate reliably:
- **Cold-tolerant (5–7°C):** Broad beans, parsnips, spinach, lettuce, onions
- **Cool season (7–10°C):** Carrots, beetroot, peas, brassicas, leeks
- **Warm season (12–15°C):** Beans, courgettes, cucumbers, sweet corn
- **Heat-loving (15–18°C):** Tomatoes, peppers, aubergines, basil

A soil thermometer is one of the most useful tools in the kitchen garden.

## Successional Sowing

Rather than sowing a whole packet of lettuce or radish at once, sow small amounts every 2–3 weeks from early spring to late summer. This provides a continuous harvest rather than a glut followed by a gap. Mark your sowings in a notebook — memory is unreliable over a long season.`,
  },
  {
    id: 'seed-types',
    category: 'Seeds & Propagation',
    emoji: '🌱',
    title: 'Understanding Seed Types',
    summary: 'Open-pollinated, F1 hybrid, heirloom, organic — what the labels on seed packets mean and why it matters to the organic grower.',
    organic: true,
    tags: ['seeds', 'varieties', 'saving-seed', 'heirloom'],
    content: `## Open-Pollinated Seeds

Open-pollinated (OP) seeds are produced by natural pollination — by wind, insects, or the plant itself — between plants of the same variety. When grown out, open-pollinated seeds produce plants true-to-type: the next generation looks and performs like the parent. This means you can save seed from open-pollinated varieties year after year, selecting over time for characteristics that suit your soil, climate, and taste.

Open-pollinated varieties are the foundation of seed saving and the preservation of agricultural biodiversity. Many have been selected over hundreds of years in specific regions and carry remarkable adaptations.

## F1 Hybrid Seeds

F1 hybrids are produced by crossing two carefully selected parent lines under controlled conditions. The first-generation (F1) offspring show "hybrid vigour" — they are often more uniform, faster-growing, and higher-yielding than either parent. However:

- F1 seeds cannot be saved reliably. If you collect seed from an F1 plant, the offspring will revert to unpredictable combinations of the grandparent traits — not the variety you grew.
- F1 varieties are replanted each year from commercially produced seed, creating dependency on seed companies.
- Many F1 varieties are bred for commercial growing conditions: uniformity of size and ripening, shelf life, resistance to mechanical harvesting — not for flavour or nutritional density.

## Heirloom Varieties

"Heirloom" has no strict legal definition but generally refers to open-pollinated varieties that are old — typically pre-1950 — and have been maintained by gardeners, seed libraries, or specialist companies. Many heirloom tomatoes, beans, and squash offer flavours unavailable in commercial varieties. They are more genetically diverse than modern commercial crops.

## Organic Seed

Organic-certified seed has been produced without synthetic pesticides or fertilisers. Regulations in many countries now require organic growers to use organic seed where it is available. Using organic seed supports the organic seed supply chain.

## Saving Your Own Seed

Saving seed from open-pollinated varieties is simple for self-pollinating crops (tomatoes, beans, peas, lettuce) and slightly more involved for cross-pollinating crops (brassicas, courgettes, beetroot) which need isolation from other varieties. Seed saving:
- Reduces cost
- Allows selection for local conditions
- Preserves genetic diversity
- Creates a personal seed collection tailored to your garden`,
  },
  {
    id: 'comfrey',
    category: 'Growing Techniques',
    emoji: '🌿',
    title: 'Comfrey as an Organic Fertiliser',
    summary: 'Why every organic garden should grow comfrey — and how to make the most potent liquid plant food from a single plant.',
    organic: true,
    tags: ['comfrey', 'liquid-feed', 'fertility', 'organic'],
    content: `## The Fertiliser Plant

Comfrey (Symphytum × uplandicum, variety 'Bocking 14') is arguably the most useful plant in the organic garden. Its deep taproot — reaching down 1.5–2 metres — draws up mineral nutrients from subsoil layers beyond the reach of most plants. The leaves accumulate potassium, nitrogen, phosphorus, and trace minerals in extraordinary concentrations. Cut and composted or steeped in water, they provide a fertiliser that rivals any commercial product.

## Bocking 14

'Bocking 14' is the variety to grow. It was selected in Bocking, Essex, by the Henry Doubleday Research Association (now Garden Organic) specifically for its high potassium content and, crucially, because it does not set viable seed. Common comfrey self-seeds prolifically and can become invasive. Bocking 14 spreads only by root, which is easily controlled.

Plant divisions (small pieces of root) in spring or autumn. Comfrey establishes quickly and will produce several cuts of leaf per season. It tolerates partial shade and grows well on the edges of the compost area, where cut leaves can be dropped directly in.

## Making Comfrey Liquid Feed

The simplest method:
1. Pack a bucket loosely with comfrey leaves.
2. Weigh them down with a brick.
3. Cover with water and leave for 4–6 weeks.
4. The resulting liquid will be dark, foul-smelling, and extraordinarily potent.
5. Dilute 1:10 with water before applying to plants.

Alternatively, pack leaves into a sealed container with a tap at the base and no water — the leaves will slowly liquify under their own weight, producing a concentrated "comfrey concentrate" that is diluted 1:40.

## Applications

The high potassium content of comfrey feed makes it particularly valuable for fruiting crops:
- Tomatoes (apply weekly once flowering begins)
- Cucumbers, courgettes, squash
- Peppers and aubergines
- Strawberries

Comfrey leaves can also be used as a mulch around potatoes, as a compost activator, or as a "chop and drop" mulch around fruit trees. Dried comfrey leaves make an excellent seed compost additive.

## Dynamic Accumulator

Comfrey is the best-known "dynamic accumulator" — a plant that mines subsoil minerals and makes them available at the surface. Other useful accumulators include borage (silica), yarrow (potassium, copper), and nettles (nitrogen, iron). A patch of stinging nettles is a genuine asset to the organic garden — nettle tea provides a nitrogen-rich feed comparable to dilute blood meal.`,
  },
  {
    id: 'permaculture',
    category: 'Soil & Planning',
    emoji: '🌍',
    title: 'An Introduction to Permaculture',
    summary: "Permaculture's core principles and how they apply to the kitchen garden — designing productive systems that work with nature rather than against it.",
    organic: true,
    tags: ['permaculture', 'design', 'sustainability', 'planning'],
    content: `## What Is Permaculture?

Permaculture — a contraction of "permanent agriculture" (later extended to "permanent culture") — is a design philosophy developed by Bill Mollison and David Holmgren in Australia in the 1970s. It is not a set of techniques but a design framework: a way of observing natural systems and creating human systems — gardens, farms, buildings, communities — that work with nature's patterns and principles rather than against them.

The core of permaculture thinking is observation before action: spend time understanding a site before changing it. Where does the water flow? Where does the wind come from? Where is it warm? Where is it cold? The answers shape everything that follows.

## The Three Ethics

All permaculture design is underpinned by three ethics:
- **Earth Care:** nurture the soil, the water, the biodiversity
- **People Care:** meet the needs of people equitably
- **Fair Share:** share surplus and return resources to the system

## Key Principles in the Garden

**Zone planning:** Divide your land into zones based on frequency of use. Zone 1 (daily use) is immediately outside the back door — herbs, salad, and frequently harvested crops. Zone 2 is the main vegetable garden. Zone 3 is fruit trees and less-tended areas. Zone 4 is semi-wild. Zone 5 is wilderness, left for nature. Design to minimise unnecessary movement.

**Stacking functions:** Each element should serve multiple functions. A hedge can be windbreak, habitat, fruit producer, and composting material. A pond can provide water, habitat, humidity, and irrigation.

**Stacking in time and space:** Plant vertically — the forest garden model layers canopy trees, smaller trees, shrubs, herbaceous plants, ground covers, and root crops in a multi-storey system that is highly productive per unit of area and closely mimics natural woodland structure.

**Catch and store energy:** Harvest rainwater, make compost, build soil organic matter — capture resources when they are abundant for use when they are scarce.

**Use edges:** The junction between two systems — the edge of woodland, the shoreline, the hedgerow — is the most diverse and productive zone. Maximise edge in garden design.

## Permaculture and the Organic Garden

Permaculture does not replace traditional organic techniques — it frames them within a coherent design philosophy. Crop rotation, companion planting, composting, and no-dig are all permaculture-compatible practices. The addition of design thinking — zone planning, observation, stacking functions — helps turn a collection of good practices into a coherent, productive system.`,
  },
  {
    id: 'rainwater-harvesting',
    category: 'Growing Techniques',
    emoji: '🌧️',
    title: 'Harvesting Rainwater for the Garden',
    summary: 'How to collect, store, and use rainwater effectively — reducing water bills, watering more sustainably, and giving your plants what they prefer.',
    organic: true,
    tags: ['rainwater', 'water', 'sustainability', 'irrigation'],
    content: `## Why Rainwater?

Mains water in the UK and many developed countries is chlorinated, fluoridated, and in hard water areas is high in calcium and magnesium. While not harmful to most plants, these additions are unnecessary and in some cases — notably for acid-loving plants like blueberries and rhododendrons — genuinely detrimental. Rainwater is naturally soft, slightly acidic, and free of chlorine. Plants, given the choice, prefer it.

Beyond water quality, collection reduces the environmental impact of irrigation: mains water requires energy to pump and treat. Harvested rainwater is free at the point of collection and uses no grid energy.

## Basic Water Butt Collection

The simplest rainwater harvesting system is a water butt connected to a downpipe from a roof:

1. Install a downpipe diverter kit (widely available from garden centres). These automatically divert flow to the butt when it is not full and return it to the drains when full.
2. Raise the water butt on blocks to allow a watering can to fit beneath the tap.
3. Cover the top with a lid or fine mesh — to prevent evaporation, exclude debris, and deny mosquitoes a breeding site.
4. A standard 200-litre water butt will fill rapidly in moderate rain. Linking two or three butts in series greatly increases capacity.

## How Much Water Does a Roof Collect?

A 1m² of roof collects approximately 0.9 litres per mm of rainfall. A typical house roof of 100m² receiving the UK average rainfall of 700mm per year could theoretically collect 63,000 litres — far more than most gardens need for irrigation.

## Larger-Scale Storage

For serious kitchen gardens or allotments, a 1,000-litre IBC tank (intermediate bulk container) — available cheaply second-hand from agricultural suppliers — provides genuine storage capacity. These can be plumbed with a simple gravity-fed drip irrigation system.

## Water Hygiene

Standing water in open butts can become anaerobic and smelly, particularly in warm weather. To prevent this:
- Keep the butt covered
- Clean it out annually
- Add a small amount of diluted hydrogen peroxide if water becomes stagnant
- Use water regularly, avoiding long periods of stagnation

Note: collected rainwater should not be used for drinking or cooking. For irrigation of vegetables, it is safe and beneficial.`,
  },
  {
    id: 'compost-bin',
    category: 'Soil & Planning',
    emoji: '🏗️',
    title: 'Building a Compost Bin',
    summary: 'How to build a simple, effective compost bin — from basic single-bay designs to the efficient three-bay system used by serious growers.',
    organic: true,
    tags: ['compost', 'building', 'fertility', 'soil'],
    content: `## Why Build Rather Than Buy?

Purpose-built compost bins from timber are larger, better-ventilated, and more effective than most plastic "dalek" bins available from garden centres. A wooden bay allows proper airflow, is easy to fill and turn, can be built to any size, and costs very little to construct — scaffolding boards, old pallets, or rough-sawn timber all work well.

## The Minimum: A Single Bay

A single compost bay of 1m × 1m × 1m (1 cubic metre) is the minimum useful size. Smaller heaps do not retain heat efficiently and decompose slowly. The bay can be three-sided with the fourth side open for access, or made with removable front boards that can be slid out when the heap is ready to turn or use.

## The Better System: Three Bays

Experienced composters use a three-bay system:

**Bay 1: The active bay.** Fresh material goes here as it is produced. Add greens and browns in layers, keep moist, keep covered.

**Bay 2: The turning bay.** When Bay 1 is full, the contents are turned into Bay 2. This aerates the heap, triggers a second heating phase, and brings the outer material to the centre. Bay 1 is now free to receive new material.

**Bay 3: The maturing bay.** Once Bay 2 has broken down, the partially composted material moves to Bay 3 to finish. Finished compost (dark, crumbly, smelling of earth) is used from here.

The system maintains a continuous supply of compost at different stages of readiness.

## Construction

The simplest three-bay system is made from scaffolding boards or rough-sawn timber:

1. Set four vertical posts (75mm × 75mm) for each bay, 1m apart front to back and 1m between bays.
2. Nail boards horizontally to the back and sides, leaving small gaps (1–2cm) between boards for airflow.
3. Leave the front open, or use removable boards slotted into vertical runners.
4. Treat timber with raw linseed oil if you want to extend its life — avoid chemical preservatives near soil and food crops.

## Location

Site the compost area in partial shade (full sun dries it out quickly) with easy access from the kitchen garden paths. It should be close enough to the garden to make regular additions convenient, but not so prominent as to be an eyesore.

## Accelerating Decomposition

- Chop or shred material before adding — smaller pieces break down faster
- Mix greens and browns as you add them; avoid adding large quantities of one material alone
- Keep the heap damp (like a wrung-out sponge, not wet)
- Turn every 4–6 weeks
- Add a thin layer of finished compost or soil between layers to inoculate with micro-organisms`,
  },
];

const CATEGORIES = ['All', 'Soil & Planning', 'Growing Techniques', 'Pest Management', 'Seeds & Propagation', 'Harvesting & Storage'];

/**
 * A single advice article at its own URL. Slugs are the article ids, so
 * /advice/companion-planting resolves directly.
 *
 * These used to be swapped in and out of the index page with local state, which
 * meant all 17 shared the /advice URL and none of them could be indexed, linked
 * to, or shared.
 */
export function AdviceArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = ARTICLES.find(a => a.id === slug);

  usePageMeta(
    article ? article.title : 'Advice Not Found',
    article
      ? article.summary
      : 'That advice article could not be found. Browse all our organic gardening guides instead.',
    article ? `/og/advice-${article.id}.png` : undefined,
  );

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!article) {
    return (
      <div className="advice-page">
        <div className="advice-article-view container" style={{ textAlign: 'center', padding: 'var(--space-20) 0' }}>
          <p style={{ fontSize: '3rem' }}>🌾</p>
          <h1 className="text-headline">Article not found</h1>
          <p className="text-body" style={{ color: 'var(--ink-light)', margin: 'var(--space-4) 0 var(--space-6)' }}>
            This guide may have been renamed or moved.
          </p>
          <Link to="/advice" className="btn-sage">Browse all advice</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="advice-page">
      <div className="advice-article-view container">
        <Link to="/advice" className="advice-back text-small">
          ← Back to all advice
        </Link>
        <article className="advice-article paper">
          <div className="advice-article-header">
            <div className="advice-article-emoji">{article.emoji}</div>
            <div>
              <p className="text-label" style={{ color: 'var(--sage)', marginBottom: 'var(--space-2)' }}>{article.category}</p>
              <h1 className="text-headline">{article.title}</h1>
              {article.organic && (
                <span className="advice-organic-badge text-caption">🌿 Organic advice</span>
              )}
            </div>
          </div>

          <div className="prose advice-prose diary-markdown">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{article.content}</ReactMarkdown>
          </div>

          <div className="advice-article-footer">
            <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
              {article.tags.map(tag => (
                <span key={tag} className="plant-tag text-caption" style={{ padding: 'var(--space-1) var(--space-3)' }}>{tag}</span>
              ))}
            </div>
          </div>
        </article>
      </div>
    </div>
  );
}

export default function AdvicePage() {
  const [activeCategory, setActiveCategory] = useState('All');

  usePageMeta(
    'Organic Gardening Advice',
    'In-depth organic gardening guides — crop rotation, composting, companion planting, no-dig beds, wormeries, seed sowing timing and winter storage.',
  );

  const filtered = activeCategory === 'All' ? ARTICLES : ARTICLES.filter(a => a.category === activeCategory);

  return (
    <div className="advice-page">
      <PageHeader
        title="Gardening Advice"
        subtitle="Practical, organic growing techniques — from soil science to companion planting, crop rotation to no-dig methods."
        eyebrow="Grow better, organically"
        decorationEmoji="📖"
      />

      <div className="container">
        {/* Category filter */}
        <div className="advice-categories section-sm">
          {CATEGORIES.map(cat => (
            <button
              key={cat}
              className={`advice-cat-btn ${activeCategory === cat ? 'advice-cat-btn--active' : ''}`}
              onClick={() => setActiveCategory(cat)}
            >
              {cat}
            </button>
          ))}
        </div>

        <div className="page-ad"><AdUnit adSlot="1570177744" width={160} height={600} /></div>
        <div className="advice-grid">
          {filtered.map((article, i) => (
            <Link
              key={article.id}
              to={`/advice/${article.id}`}
              className={`advice-card animate-fade-in-delay-${Math.min(i + 1, 5)}`}
            >
              <div className="advice-card-emoji">{article.emoji}</div>
              <div className="advice-card-body">
                <p className="text-label" style={{ color: 'var(--sage)', marginBottom: 'var(--space-1)' }}>{article.category}</p>
                <h3 className="text-title" style={{ marginBottom: 'var(--space-2)' }}>{article.title}</h3>
                <p className="text-small" style={{ color: 'var(--ink-light)', lineHeight: 1.6 }}>{article.summary}</p>
                {article.organic && <span className="advice-organic-badge text-caption" style={{ marginTop: 'var(--space-3)', display: 'inline-block' }}>🌿 Organic</span>}
              </div>
              <span className="advice-card-arrow">→</span>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
